import React, { useState, useEffect } from "react";
import {
  Bell,
  Settings,
  User,
  Plus,
  Edit,
  Save,
  X,
  Bot,
  Hourglass,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import checkPermission from "../utils/permission";
import { useTheme } from "../contexts/ThemeContext";
import { ApiService } from "../services/api";
import InstallAppButton from "../components/ui/InstallAppButton";
import {
  ACTIVE_FOCUS_SESSION_EVENT,
  clearActiveFocusSessionSummary,
  formatSeconds,
  getLiveFocusSessionSnapshot,
  normalizeActiveFocusSessionSummary,
  readActiveFocusSessionSummary,
  storeActiveFocusSessionSummary,
} from "../utils/timeTracking";

const TopNavbar = ({
  toggleSidebar,
  toggleAIPanel,
  isEditMode,
  toggleEditMode,
  cancelEditMode,
}: {
  toggleSidebar: () => void;
  toggleAIPanel: () => void;
  isEditMode: boolean;
  toggleEditMode: () => void;
  cancelEditMode: () => void;
}) => {
  const [focusSession, setFocusSession] = useState(() => readActiveFocusSessionSummary());
  const [, setFocusTick] = useState(0);

  const [showAIBubble, setShowAIBubble] = useState(() => {
    const bubbleDismissed = localStorage.getItem("aiBubbleDismissed");
    return bubbleDismissed !== "true";
  });

  const [unreadCount, setUnreadCount] = useState(0);


  const dismissAIBubble = () => {
    setShowAIBubble(false);
    localStorage.setItem("aiBubbleDismissed", "true");
  };

  const { user, systemConfig } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  const canViewTimeCenter = checkPermission("time_tracking_list");
  const liveFocusSession = getLiveFocusSessionSnapshot(focusSession);

  useEffect(() => {
    const handleFocusSessionChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      setFocusSession(normalizeActiveFocusSessionSummary(customEvent.detail) || null);
    };
    window.addEventListener(ACTIVE_FOCUS_SESSION_EVENT, handleFocusSessionChange);
    return () => window.removeEventListener(ACTIVE_FOCUS_SESSION_EVENT, handleFocusSessionChange);
  }, []);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setFocusTick((value) => value + 1);
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!user?.id || !canViewTimeCenter) {
      clearActiveFocusSessionSummary();
      setFocusSession(null);
      return;
    }

    let mounted = true;

    const loadActiveSession = async () => {
      const response = await ApiService.getMyFocusSessions({ status: "active", page: 1, limit: 1 }).catch(() => null);
      if (!mounted) return;
      const normalized = normalizeActiveFocusSessionSummary(response?.data?.[0] || null);
      setFocusSession(normalized);
      if (normalized) {
        storeActiveFocusSessionSummary(normalized);
        return;
      }
      clearActiveFocusSessionSummary();
    };

    void loadActiveSession();

    return () => {
      mounted = false;
    };
  }, [user?.id, canViewTimeCenter]);

  const openTimeCenter = () => {
    window.dispatchEvent(new CustomEvent("toggleTimerPanel"));
    setShowAIBubble(false);
  };

  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const response = await ApiService.getUnreadNotificationCount();
        setUnreadCount(response.unreadCount || 0);
      } catch (error) {
        console.error("Failed to fetch unread count:", error);
      }
    };

    if (user && typeof (user as any).unreadNotifications === 'number') setUnreadCount((user as any).unreadNotifications);
    else fetchUnreadCount();

    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const toggleNotificationsPanel = () => {
    window.dispatchEvent(new CustomEvent("toggleNotificationsPanel"));
    setShowAIBubble(false);
  };

  const toggleSettingsPanel = () => {
    window.dispatchEvent(new CustomEvent("toggleSettingsPanel"));
  };

  return (
    <>
      <div
        className="fixed top-0 left-0 right-0 h-[60px] flex items-center justify-between pr-2 sm:pr-4 z-[999] backdrop-blur-md border-b border-white/15"
        style={{
          backgroundColor: `${currentTheme.colors.background.base}e6`,
        }}>
        <div className="flex items-center">
          <button
            onClick={() => {
              toggleSidebar();
              setShowAIBubble(false);
            }}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white ml-2 mr-2 sm:mr-4 transition-all duration-200"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
            <div className="grid grid-cols-3 gap-[2px]">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="w-[6px] h-[6px] bg-white rounded-sm"></div>
              ))}
            </div>
          </button>

          <div
            className="flex items-center mr-2 sm:mr-6 min-w-0 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate("/dashboard")}>
            <img
              src={systemConfig?.companyLogo || "/logo.svg"}
              alt={systemConfig?.companyName || "Yeni Eğitim Sistemi"}
              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover shadow-md"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                if (target.src.includes("favicon")) return;
                target.src = "/favicon/favicon-96x96.png";
              }}
            />
            <div className="ml-2 text-white leading-tight">
              <div className="text-sm sm:text-lg font-semibold drop-shadow-lg md:drop-shadow-none">
                {systemConfig?.companyName || "Yeni Eğitim Sistemi"}
              </div>
              <div className="text-[8px] sm:text-[10px] text-gray-400 font-semibold tracking-wide drop-shadow-lg md:drop-shadow-none"></div>
            </div>
          </div>

          <div className="hidden sm:flex items-center space-x-2">
            <button
              className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation opacity-50 cursor-not-allowed"
              style={{
                backgroundColor: `${currentTheme.colors.primary}33`,
                border: `1px solid ${currentTheme.colors.primary}66`,
              }}
              disabled
              onClick={(e) => e.preventDefault()}>
              <Plus size={18} />
            </button>
            {!isEditMode ? (
              <button
                onClick={toggleEditMode}
                className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation"
                style={{
                  backgroundColor: `${currentTheme.colors.primary}33`,
                  border: `1px solid ${currentTheme.colors.primary}66`,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}4d`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                }}
                title="Sıralamayı Düzenle">
                <Edit size={18} />
              </button>
            ) : (
              <>
                <button
                  onClick={toggleEditMode}
                  className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation"
                  style={{
                    backgroundColor: `${currentTheme.colors.secondary}33`,
                    border: `1px solid ${currentTheme.colors.secondary}66`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.secondary}4d`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `${currentTheme.colors.secondary}33`;
                  }}
                  title="Sıralamayı Kaydet">
                  <Save size={18} />
                </button>
                <button
                  onClick={cancelEditMode}
                  className="w-10 h-10 flex items-center justify-center text-white ml-2 rounded-full transition-all duration-300 hover:scale-110 touch-manipulation"
                  style={{
                    backgroundColor: `#ef444433`,
                    border: `1px solid #ef444466`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = `#ef44444d`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = `#ef444433`;
                  }}
                  title="İptal">
                  <X size={18} />
                </button>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-1 sm:space-x-3">
          <InstallAppButton />

          <div className="relative">
            <button
              onClick={() => {
                toggleAIPanel();
                setShowAIBubble(false);
              }}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white border border-white text-xs font-bold rounded hover:bg-white/20 transition-colors touch-manipulation">
              AI
            </button>
            {showAIBubble && isDashboard && (
              <div
                className="absolute top-full mt-2 left-0 whitespace-nowrap z-50 cursor-pointer"
                onClick={() => {
                  toggleAIPanel();
                  dismissAIBubble();
                }}>
                <div
                  className="relative text-white text-xs font-medium px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm"
                  style={{
                    background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                    border: `1px solid ${currentTheme.colors.accent}66`,
                  }}>
                  <div className="flex items-center gap-2">
                    <Bot size={14} className="text-white" />
                    <div>
                      <div className="font-semibold">Merhaba {user?.firstName || "Kullanıcı"}!</div>
                      <div className="text-[10px] opacity-90">Deep asistanınız burada.</div>
                    </div>
                  </div>
                  <div
                    className="absolute -top-2 left-3 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-b-[8px]"
                    style={{ borderBottomColor: currentTheme.colors.primary }}></div>
                </div>
              </div>
            )}
          </div>

          {canViewTimeCenter ? (
            <div className="relative">
              <button
                onClick={openTimeCenter}
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
                title="Zaman Merkezi"
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "transparent";
                }}>
                <Hourglass size={16} />
              </button>
              {liveFocusSession && (() => {
                const baseValue =
                  liveFocusSession.type === "stopwatch"
                    ? liveFocusSession.liveElapsedSeconds
                    : liveFocusSession.liveRemainingSeconds || 0;
                const prefix =
                  liveFocusSession.type === "stopwatch"
                    ? "KR "
                    : liveFocusSession.type === "pomodoro"
                      ? "PO "
                      : "";
                const label = `${prefix}${formatSeconds(baseValue)}${liveFocusSession.status === "paused" ? " • D" : ""}`;
                return (
                  <div
                    className="absolute top-full mt-1 left-1/2 -translate-x-1/2 whitespace-nowrap z-50 cursor-pointer"
                    onClick={openTimeCenter}>
                    <div
                      className="text-white text-[11px] font-bold px-2 py-0.5 rounded-md shadow-lg"
                      style={{
                        background: `linear-gradient(135deg, ${currentTheme.colors.primary}, ${currentTheme.colors.secondary})`,
                        border: `1px solid ${currentTheme.colors.accent}66`,
                        fontVariantNumeric: "tabular-nums",
                      }}>
                      {label}
                    </div>
                    <div
                      className="absolute -top-1 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-l-transparent border-r-[5px] border-r-transparent border-b-[6px]"
                      style={{ borderBottomColor: currentTheme.colors.primary }}
                    />
                  </div>
                );
              })()}
            </div>
          ) : null}

          <div className="relative">
            <button
              onClick={toggleNotificationsPanel}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation relative"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}>
              <Bell size={16} />
              {unreadCount > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white rounded-full px-1"
                  style={{
                    backgroundColor: "#ef4444",
                    boxShadow: "0 2px 4px rgba(239, 68, 68, 0.4)",
                  }}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>
          <button
            onClick={() => {
              toggleSettingsPanel();
              setShowAIBubble(false);
            }}
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
            <Settings size={16} />
          </button>
          <div className="relative">
            <button
              onClick={() => {
                window.dispatchEvent(new CustomEvent("toggleUserProfile"));
                setShowAIBubble(false);
              }}
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center text-white rounded transition-all duration-200 touch-manipulation"
              title="Profili Düzenle"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${currentTheme.colors.primary}33`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
              }}>
              <User size={16} />
            </button>
          </div>
        </div>
      </div>

    </>
  );
};

export default TopNavbar;
